/**
 * SAUVEGARDES — ce que l'écran calcule seul, sans React (testable tel quel).
 *
 * `baissesDeProtection` est la COPIE de celle du serveur
 * (packages/core/src/system/backup-settings.ts) : le navigateur n'importe
 * pas le cœur. Elle sert à dire, AVANT d'enregistrer, ce qui va demander
 * une confirmation d'identité — sinon le dialogue générique « opération
 * sensible » s'ouvre sans que personne sache quel changement l'a provoqué.
 * Le serveur reste seul juge ; tests/sauvegarde-lot-b.test.ts vérifie que
 * les deux disent la même chose.
 */

export interface ReglagesProtection {
  enabled: boolean
  slots: { time: string; files: boolean }[]
  retention: { daily_days: number; monthly_months: number }
  restore_test: { enabled: boolean; weekday: number; time: string }
  alert_after_hours: number
  restore_test_alert_days: number
  /** Lot C : la copie que le PC du bureau vient chercher. */
  pc: { enabled: boolean; keep: number; alert_after_days: number }
}

/**
 * « 12 Ko », « 3 Mo », « 1,2 Go ». Jamais « 0 Ko » : la clé maître pèse
 * quelques dizaines d'octets, et « Clé maître 0 Ko » se lit « copie vide ».
 */
export function taille(o: number): string {
  if (o >= 1e9) return `${(o / 1e9).toFixed(1).replace('.', ',')} Go`
  if (o >= 1e6) return `${Math.round(o / 1e6)} Mo`
  if (o > 0 && o < 500) return '< 1 Ko'
  return `${Math.round(o / 1e3)} Ko`
}

/** « aujourd'hui à 07h30 », « demain à 07h30 », « le 20/10 à 08h00 » — l'heure du club. */
export function quandRelatif(iso: string, maintenant = new Date()): string {
  const d = new Date(iso)
  const jour = (x: Date) => x.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
  const h = d.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).replace(':', 'h')
  if (jour(d) === jour(maintenant)) return `aujourd’hui à ${h}`
  if (jour(d) === jour(new Date(maintenant.getTime() + 86_400_000))) return `demain à ${h}`
  if (jour(d) === jour(new Date(maintenant.getTime() - 86_400_000))) return `hier à ${h}`
  return `le ${d.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit' })} à ${h}`
}

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche']
function nomJour(weekday: number): string {
  return JOURS[(weekday - 1 + 7) % 7] ?? '?'
}
function heureLisible(time: string): string {
  return time.replace(':', 'h')
}

export function baissesDeProtection(avant: ReglagesProtection, apres: ReglagesProtection): string[] {
  const out: string[] = []
  if (avant.enabled && !apres.enabled) out.push('la sauvegarde automatique est désactivée')
  for (const c of avant.slots) {
    const n = apres.slots.find((x) => x.time === c.time)
    if (!n) out.push(`la sauvegarde de ${heureLisible(c.time)} est retirée`)
    else if (c.files && !n.files) out.push(`la sauvegarde de ${heureLisible(c.time)} n’inclut plus les fichiers`)
  }
  if (apres.retention.daily_days < avant.retention.daily_days) {
    out.push(`les quotidiennes passent de ${avant.retention.daily_days} à ${apres.retention.daily_days} jours`)
  }
  if (apres.retention.monthly_months < avant.retention.monthly_months) {
    out.push(`les mensuelles passent de ${avant.retention.monthly_months} à ${apres.retention.monthly_months} mois`)
  }
  if (avant.restore_test.enabled && !apres.restore_test.enabled) out.push('le test de restauration hebdomadaire est désactivé')
  else if (avant.restore_test.enabled && apres.restore_test.enabled
    && (avant.restore_test.weekday !== apres.restore_test.weekday || avant.restore_test.time !== apres.restore_test.time)) {
    out.push(`le test de restauration passe du ${nomJour(avant.restore_test.weekday)} ${heureLisible(avant.restore_test.time)} `
      + `au ${nomJour(apres.restore_test.weekday)} ${heureLisible(apres.restore_test.time)}`)
  }
  if (apres.alert_after_hours > avant.alert_after_hours) {
    out.push(`l’alerte « aucune sauvegarde » attend ${apres.alert_after_hours} h au lieu de ${avant.alert_after_hours} h`)
  }
  if (apres.restore_test_alert_days > avant.restore_test_alert_days) {
    out.push(`l’alerte « test trop ancien » attend ${apres.restore_test_alert_days} j au lieu de ${avant.restore_test_alert_days} j`)
  }
  if (avant.pc.enabled && !apres.pc.enabled) out.push('la copie sur le PC du bureau est désactivée')
  if (apres.pc.keep < avant.pc.keep) {
    out.push(`le PC du bureau garde ${apres.pc.keep} copies au lieu de ${avant.pc.keep}`)
  }
  if (apres.pc.alert_after_days > avant.pc.alert_after_days) {
    out.push(`l’alerte « PC du bureau » attend ${apres.pc.alert_after_days} j au lieu de ${avant.pc.alert_after_days} j`)
  }
  return out
}
